import React, { useState } from 'react';
import { 
  FaBookReader, 
  FaPlayCircle, 
  FaCheckCircle, 
  FaClock, 
  FaCode, 
  FaLightbulb, 
  FaArrowRight,
  FaQuestionCircle 
} from 'react-icons/fa';

// Mock active learning sprint generated dynamically by the LLM pipeline
const MOCK_ACTIVE_SYLLABUS = {
  targetSkill: "LLM Structured Extraction",
  estimatedTime: "4-6 Hours",
  totalModules: 3,
  modules: [
    {
      id: "mod-1",
      title: "1. Schema Definitions & JSON Schema constraints",
      duration: "1.5 hours",
      status: "completed",
      summary: "Mastering Pydantic or Zod mappings to enforce strict structure on non-deterministic LLM responses."
    },
    {
      id: "mod-2",
      title: "2. Zero-Shot Function Calling vs. Instructor Patterns",
      duration: "2 hours",
      status: "active",
      summary: "Configuring native system calls against application middleware processing to catch schema dropouts."
    },
    {
      id: "mod-3",
      title: "3. Validation Error Handling & Repair Loops",
      duration: "2 hours",
      status: "locked",
      summary: "Designing automated retry prompts feeding parsing exceptions back to the LLM for self-correction."
    }
  ]
};

// Simulated interaction states for the verification quiz loop
const MOCK_QUIZ_PROMPT = {
  question: "When an LLM drops a required key from your schema during a structured output response, what is the most efficient architectural repair loop before throwing an application error?",
  options: [
    { id: "A", text: "Fail the request instantly and force the client user to manually re-type their submission." },
    { id: "B", text: "Catch the validation exception, append the error message to a secondary LLM retry prompt, and pass the partial output back to repair the missing keys." },
    { id: "C", text: "Write a fallback regex parser to try and guess where the missing data should have been inside the raw string." }
  ]
};

export default function LearningEngineView() {
  const [activeModule, setActiveModule] = useState("mod-2");
  const [taskLogInput, setTaskLogInput] = useState("Fixed a schema dropout bug in the pipeline where optional nested fields were missing from the json structure.");
  const [showQuiz, setShowQuiz] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [quizVerified, setQuizVerified] = useState(false);

  const handleLogTask = (e) => {
    e.preventDefault();
    if (taskLogInput.trim()) {
      setShowQuiz(true);
      setQuizVerified(false);
      setSelectedAnswer(null);
    }
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === "B") {
      setQuizVerified(true);
    } else {
      alert("Incorrect logic. Hint: Leverage the model's contextual memory to repair structures rather than breaking execution loops.");
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.engineLayout}>
        
        {/* Left Panel: Syllabus & Progression Roadmap */}
        <section style={styles.syllabusPanel}>
          <div style={styles.panelHeader}>
            <div style={styles.badge}>Active Sprint</div>
            <h2 style={styles.mainTitle}>{MOCK_ACTIVE_SYLLABUS.targetSkill} Syllabus</h2>
            <div style={styles.metaRow}>
              <span style={styles.metaItem}><FaClock /> {MOCK_ACTIVE_SYLLABUS.estimatedTime}</span>
              <span style={styles.metaItem}><FaBookReader /> {MOCK_ACTIVE_SYLLABUS.totalModules} Modules</span>
            </div>
          </div>

          <div style={styles.moduleTimeline}>
            {MOCK_ACTIVE_SYLLABUS.modules.map((mod) => {
              const isActive = mod.id === activeModule;
              const isCompleted = mod.status === 'completed';
              
              return (
                <div 
                  key={mod.id} 
                  onClick={() => mod.status !== 'locked' && setActiveModule(mod.id)}
                  style={{ 
                    ...styles.moduleCard, 
                    ...(isActive ? styles.activeModuleCard : {}),
                    ...(mod.status === 'locked' ? styles.lockedModuleCard : {})
                  }}
                >
                  <div style={styles.moduleTopRow}>
                    <div style={styles.titleBlock}>
                      {isCompleted ? (
                        <FaCheckCircle style={styles.completedIcon} />
                      ) : isActive ? (
                        <FaPlayCircle style={styles.activeIcon} />
                      ) : (
                        <div style={styles.lockedDot} />
                      )}
                      <h4 style={{ ...styles.moduleTitleText, ...(isActive ? styles.activeText : {}) }}>
                        {mod.title}
                      </h4>
                    </div>
                    <span style={styles.durationText}>{mod.duration}</span>
                  </div>
                  <p style={styles.moduleDesc}>{mod.summary}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Right Panel: Task Execution Playground & Micro-Validation Quiz */}
        <section style={styles.executionPanel}>
          <div style={styles.sectionHeaderBox}>
            <FaCode style={styles.sectionIcon} />
            <h3 style={styles.sectionTitle}>Verification & Task Logging</h3>
          </div>
          
          <p style={styles.contextInstructionText}>
            Log your real-world coding task or active project build down below to kick off your contextual validation quiz.
          </p>

          <form onSubmit={handleLogTask} style={styles.taskForm}>
            <textarea
              style={styles.taskTextarea}
              value={taskLogInput}
              onChange={(e) => setTaskLogInput(e.target.value)}
              placeholder="What did you build or resolve just now?"
            />
            <button type="submit" style={styles.logTaskBtn}>
              Log Task & Trigger Validation <FaArrowRight style={{ marginLeft: '6px' }} />
            </button>
          </form>

          {/* Micro-Validation Interface Area */}
          {showQuiz && (
            <div style={{ ...styles.quizContainer, ...(quizVerified ? styles.quizSuccessContainer : {}) }}>
              <div style={styles.quizHeaderRow}>
                <FaQuestionCircle style={quizVerified ? styles.quizIconSuccess : styles.quizIconPending} />
                <span style={styles.quizTitleText}>
                  {quizVerified ? "Verification Secured (+1 Block)" : "Instant Contextual Quiz"}
                </span>
              </div>
              
              {!quizVerified ? (
                <>
                  <p style={styles.quizQuestionText}>{MOCK_QUIZ_PROMPT.question}</p>
                  <div style={styles.optionsList}>
                    {MOCK_QUIZ_PROMPT.options.map((option) => (
                      <label 
                        key={option.id} 
                        style={{ 
                          ...styles.optionLabel, 
                          ...(selectedAnswer === option.id ? styles.selectedOptionLabel : {}) 
                        }}
                      >
                        <input
                          type="radio"
                          name="quiz-option"
                          value={option.id}
                          checked={selectedAnswer === option.id}
                          onChange={() => setSelectedAnswer(option.id)}
                          style={styles.radioHidden}
                        />
                        <span style={styles.optionMarker}>{option.id}</span>
                        <span style={styles.optionContentText}>{option.text}</span>
                      </label>
                    ))}
                  </div>
                  <button 
                    onClick={handleSubmitAnswer}
                    disabled={!selectedAnswer}
                    style={styles.submitAnswerBtn}
                  >
                    Verify Competency Block
                  </button>
                </>
              ) : (
                <div style={styles.successBlock}>
                  <p style={styles.successMessageText}>
                    Excellent. Feeding the schema error back into the contextual window as an iterative repair loop bypasses hard app crashes and allows the LLM to complete structured outputs accurately.
                  </p>
                  <div style={styles.xpBadgeRow}>
                    <FaLightbulb style={{ marginRight: '6px' }} /> +50 Skill XP Logged to Profile Schema
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0d1117',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  },
  engineLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    maxWidth: '1240px',
    margin: '0 auto'
  },
  syllabusPanel: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  panelHeader: {
    borderBottom: '1px solid #21262d',
    paddingBottom: '16px'
  },
  badge: {
    display: 'inline-block',
    backgroundColor: 'rgba(56, 139, 253, 0.1)',
    color: '#58a6ff',
    border: '1px solid rgba(56, 139, 253, 0.2)',
    borderRadius: '20px',
    padding: '2px 10px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: '8px'
  },
  mainTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#f0f6fc',
    margin: '0 0 8px 0'
  },
  metaRow: {
    display: 'flex',
    gap: '16px',
    fontSize: '13px',
    color: '#8b949e'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  moduleTimeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  moduleCard: {
    backgroundColor: '#0d1117',
    border: '1px solid #21262d',
    borderRadius: '6px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  activeModuleCard: {
    borderColor: '#388bfd',
    boxShadow: '0 0 0 1px #388bfd'
  },
  lockedModuleCard: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  moduleTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  titleBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  completedIcon: {
    color: '#238636',
    fontSize: '14px'
  },
  activeIcon: {
    color: '#58a6ff',
    fontSize: '14px'
  },
  lockedDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#30363d',
    margin: '3px'
  },
  moduleTitleText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#c9d1d9',
    margin: 0
  },
  activeText: {
    color: '#58a6ff'
  },
  durationText: {
    fontSize: '11px',
    color: '#8b949e',
    backgroundColor: '#21262d',
    padding: '2px 6px',
    borderRadius: '4px'
  },
  moduleDesc: {
    margin: 0,
    fontSize: '12px',
    color: '#8b949e',
    lineHeight: '1.5',
    paddingLeft: '24px'
  },
  executionPanel: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  sectionHeaderBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  sectionIcon: {
    color: '#8b949e',
    fontSize: '16px'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: 0
  },
  contextInstructionText: {
    margin: 0,
    fontSize: '13px',
    color: '#8b949e',
    lineHeight: '1.5'
  },
  taskForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  taskTextarea: {
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '12px',
    color: '#c9d1d9',
    fontSize: '13px',
    fontFamily: 'monospace',
    height: '80px',
    resize: 'none',
    outline: 'none',
    lineHeight: '1.5'
  },
  logTaskBtn: {
    backgroundColor: '#238636',
    color: '#ffffff',
    border: '1px solid #2ea44f',
    borderRadius: '6px',
    padding: '10px 16px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s'
  },
  quizContainer: {
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '8px'
  },
  quizSuccessContainer: {
    borderColor: '#238636',
    backgroundColor: 'rgba(35, 134, 54, 0.05)'
  },
  quizHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderBottom: '1px solid #21262d',
    paddingBottom: '10px'
  },
  quizIconPending: {
    color: '#f0883e',
    fontSize: '16px'
  },
  quizIconSuccess: {
    color: '#238636',
    fontSize: '16px'
  },
  quizTitleText: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#f0f6fc'
  },
  quizQuestionText: {
    margin: 0,
    fontSize: '13px',
    color: '#c9d1d9',
    lineHeight: '1.5',
    fontWeight: '500'
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  optionLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '10px 12px',
    cursor: 'pointer',
    transition: 'all 0.15s'
  },
  selectedOptionLabel: {
    borderColor: '#58a6ff',
    backgroundColor: 'rgba(56, 139, 253, 0.05)'
  },
  radioHidden: {
    display: 'none'
  },
  optionMarker: {
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#58a6ff',
    backgroundColor: '#21262d',
    padding: '2px 6px',
    borderRadius: '4px',
    marginTop: '1px'
  },
  optionContentText: {
    fontSize: '12px',
    color: '#c9d1d9',
    lineHeight: '1.4'
  },
  submitAnswerBtn: {
    backgroundColor: '#21262d',
    color: '#c9d1d9',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    alignSelf: 'flex-end',
    transition: 'all 0.2s'
  },
  successBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  successMessageText: {
    margin: 0,
    fontSize: '12px',
    color: '#c9d1d9',
    lineHeight: '1.5'
  },
  xpBadgeRow: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '12px',
    color: '#238636',
    fontWeight: '600'
  }
};